import { AlertCircle, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import { date, eur, hours, monthLabel } from "@/lib/format";
import type { Dashboard } from "@/lib/types";
import { cn } from "@/lib/utils";
import { daysBetween, deltaPct } from "./chart";

const STALE_DAYS = 60;

function Card({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("flex min-w-0 flex-col gap-1 rounded-xl bg-card p-4 ring-1 ring-border", className)}>
      <h2 className="text-sm text-muted-foreground">{label}</h2>
      {children}
    </section>
  );
}

export function KpiCards({ d, today }: { d: Dashboard; today: string }) {
  const { totals } = d.open;
  const cur = d.current;
  const delta = deltaPct(cur.amountEur, cur.previous.amountEur);
  const age = totals.oldest ? daysBetween(totals.oldest.date, today) : null;
  const stale = age !== null && age >= STALE_DAYS;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Card label="Offen zu verrechnen" className="col-span-2 lg:col-span-1">
        <p className="text-3xl font-semibold tracking-tight tabular-nums">{eur(totals.totalEur)}</p>
        <p className="text-sm text-muted-foreground tabular-nums">
          {hours(totals.billableMinutes)} in {totals.entryCount} {totals.entryCount === 1 ? "Buchung" : "Buchungen"}
          {totals.expenseEur > 0 && <> · {eur(totals.expenseEur)} Ausgaben</>}
        </p>
      </Card>

      <Card label={monthLabel(cur.month)}>
        <p className="text-2xl font-semibold tabular-nums">{eur(cur.amountEur)}</p>
        <p className="flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground tabular-nums">
          {hours(cur.billableMinutes)}
          {delta !== null && (
            <span className="inline-flex items-center gap-0.5">
              {delta >= 0 ? <ArrowUpRight aria-hidden className="size-3.5" /> : <ArrowDownRight aria-hidden className="size-3.5" />}
              {delta >= 0 ? "+" : "−"}{Math.abs(delta)} % zu {monthLabel(cur.previous.month).split(" ")[0]}
            </span>
          )}
        </p>
      </Card>

      <Card label="Ältester offener Posten">
        {totals.oldest ? (
          <>
            <p className={cn("flex items-center gap-1.5 text-2xl font-semibold tabular-nums", stale && "text-warning")}>
              {stale && <AlertCircle aria-hidden className="size-5 shrink-0" />}
              {age === 0 ? "heute" : `${age} Tage`}
            </p>
            <p className="truncate text-sm text-muted-foreground" title={totals.oldest.clientName}>
              seit {date(totals.oldest.date)} · {totals.oldest.clientName}
              {stale && <span className="sr-only"> (überfällig, älter als {STALE_DAYS} Tage)</span>}
            </p>
          </>
        ) : <p className="text-2xl font-semibold">–</p>}
      </Card>

      <Card label="Ausgaben ohne Zuordnung">
        <p className="text-2xl font-semibold tabular-nums">{d.expensesUnassigned.count}</p>
        {d.expensesUnassigned.count > 0 ? (
          <Link to="/ausgaben" className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
            {eur(d.expensesUnassigned.amountEur)} · zuordnen →
          </Link>
        ) : <p className="text-sm text-muted-foreground">alles zugeordnet</p>}
      </Card>

      <Card label="Lange kein Kontakt" className={cn(!d.staleContacts.mailsConnected && "opacity-80")}>
        <p className="text-2xl font-semibold tabular-nums">{d.staleContacts.count}</p>
        {d.staleContacts.top.length > 0 ? (
          <ul className="space-y-0.5 text-sm text-muted-foreground">
            {d.staleContacts.top.map((s) => (
              <li key={s.id} className="truncate">
                <Link to={`/firmen/${s.id}`} className="hover:text-foreground">{s.name}</Link>
                <span className="tabular-nums"> · {s.daysSinceContact === null ? "noch nie" : `${s.daysSinceContact} Tage`}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-muted-foreground">alle in Kontakt</p>}
        {!d.staleContacts.mailsConnected && <p className="text-xs text-muted-foreground">zählt Notizen – Mails aus HEY folgen</p>}
      </Card>
    </div>
  );
}
